import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, Trophy, MapPin, ArrowRight } from "lucide-react";

/**
 * Gold-tinted horizontal trophy carousel showing only Golden Drop Winners.
 * Auto-plays (CSS scroll) and pauses on hover.
 */
export default function GoldenWinnersCarousel() {
  const [winners, setWinners] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/winners", {
          params: { only_golden: true, limit: 20 },
        });
        setWinners(data.winners || []);
      } catch {
        setWinners([]);
      }
    })();
  }, []);

  if (winners.length === 0) return null;

  // Duplicate for seamless scroll
  const track = [...winners, ...winners];

  return (
    <section
      className="relative overflow-hidden border -mx-4 md:-mx-8 py-6 md:py-8"
      style={{
        borderColor: "#D7A62A",
        background:
          "linear-gradient(90deg, rgba(215,166,42,0.12), rgba(15,15,15,0.6), rgba(215,166,42,0.12))",
      }}
      data-testid="golden-winners-carousel"
    >
      <div className="flex items-center justify-between px-6 md:px-8 mb-4">
        <div className="flex items-center gap-2">
          <Crown size={18} style={{ color: "#D7A62A" }} />
          <span
            className="font-display uppercase text-xl md:text-2xl tracking-wide"
            style={{ color: "#D7A62A" }}
          >
            Golden Drop Winners
          </span>
        </div>
        <Link to="/golden" className="font-mono text-xs text-[#D7A62A] flex items-center gap-1" data-testid="golden-carousel-link">
          See golden drops <ArrowRight size={12} />
        </Link>
      </div>

      <div className="loot-marquee-track">
        <div className="flex gap-4 loot-marquee-inner px-6 md:px-8">
          {track.map((w, i) => (
            <div
              key={`${w.id}-${i}`}
              className="loot-card-flat shrink-0 w-72 p-5 relative overflow-hidden"
              style={{
                borderColor: "#D7A62A",
                background:
                  "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 60%)",
              }}
              data-testid={`golden-carousel-card-${i}`}
            >
              <Crown
                size={80}
                aria-hidden="true"
                className="absolute -right-3 -top-3 opacity-10"
                style={{ color: "#D7A62A" }}
              />
              <div className="flex items-center gap-2 relative">
                <Trophy size={14} style={{ color: "#D7A62A" }} />
                <span
                  className="font-mono text-[10px] uppercase tracking-widest"
                  style={{ color: "#D7A62A" }}
                >
                  Golden Winner
                </span>
              </div>
              <div className="font-display uppercase text-2xl mt-2 leading-tight">
                @{w.username || w.name}
              </div>
              <div
                className="font-display uppercase text-2xl mt-1"
                style={{ color: "#D7A62A" }}
              >
                {w.is_mystery
                  ? "🎁 Mystery Loot"
                  : (w.prize_type && w.prize_type !== "cash")
                    ? `Found ${w.prize_custom_label || w.prize_type.replace(/_/g, " ")}`
                    : (w.hide_amount ? "Found CASH" : `Found $${w.amount}`)}
              </div>
              <div className="mt-3 font-mono text-xs text-[#F5F2E8]/60 flex items-center gap-1">
                <MapPin size={12} /> {w.city}
              </div>
              {w.sponsor && (
                <div className="mt-2 font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
                  via {w.sponsor.business_name}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
